/* The agent's secrets: keys its code needs, as environment variables on the machines of
   the team's chats and scheduled tasks — never for people on channels or the API. A value
   goes in and never comes back: the list shows a name and its last four characters. */
import { useState } from "react";
import { KeyRound, Plus, Trash2 } from "lucide-react";

import { useAgentSecrets, useDeleteSecret, usePutSecret } from "@/shared/api/agents";
import { ago } from "@/shared/lib/time";
import { Counted, ListCard, ListEmpty, ListRow, ListTile } from "@/shared/components/ListCard";
import { BrandButton, GhostButton, IconButton } from "@/shared/components/ui-kit";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";

import type { AgentSecret } from "@/shared/types";

const NAME = /^[A-Z][A-Z0-9_]{0,63}$/;
const fieldCls =
  "min-w-0 h-8 px-2.5 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus:ring-[var(--hairline-focus)] outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]";

function AddSecret({ agentId, onDone }: { agentId: string; onDone: () => void }) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const put = usePutSecret();
  const valid = NAME.test(name) && value.length > 0;
  const save = () => put.mutate({ id: agentId, name, value }, { onSuccess: onDone });
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3">
      <input
        aria-label="Secret name"
        placeholder="GH_TOKEN"
        value={name}
        onChange={(e) => setName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
        className={`${fieldCls} font-mono basis-[140px] flex-1`}
      />
      <input
        aria-label="Secret value"
        type="password"
        autoComplete="off"
        placeholder="Value"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className={`${fieldCls} basis-[200px] flex-[2]`}
      />
      <div className="flex items-center gap-2">
        <GhostButton onClick={onDone}>Cancel</GhostButton>
        <BrandButton onClick={save} disabled={!valid || put.isPending} className="disabled:opacity-40 disabled:pointer-events-none">
          Save
        </BrandButton>
      </div>
    </div>
  );
}

function SecretRow({ secret, onDelete }: { secret: AgentSecret; onDelete: () => void }) {
  return (
    <ListRow
      lead={<ListTile><KeyRound className="w-[14px] h-[14px]" strokeWidth={1.75} /></ListTile>}
      title={<span className="font-mono">{secret.name}</span>}
      desc={<><span className="font-mono tabular-nums">••••{secret.hint}</span> · set {ago(secret.updated_at)}</>}
    >
      <IconButton onClick={onDelete} aria-label={`Delete ${secret.name}`} title="Delete">
        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
      </IconButton>
    </ListRow>
  );
}

export default function SandboxSecrets({ agentId }: { agentId: string }) {
  const { data: secrets = [] } = useAgentSecrets(agentId);
  const remove = useDeleteSecret();
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  return (
    <>
      <ListCard
        label={<Counted n={secrets.length} noun="secret" />}
        action={!adding && (
          <GhostButton onClick={() => setAdding(true)} className="h-7 px-2.5">
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add secret
          </GhostButton>
        )}
      >
        {adding && <AddSecret agentId={agentId} onDone={() => setAdding(false)} />}
        {secrets.length === 0 && !adding && (
          <ListEmpty
            icon={KeyRound}
            title="No secrets"
            hint="Keys this agent's code needs, like GH_TOKEN. Your team's chats and scheduled tasks get them as environment variables — people on channels or the API never do. The agent can read them."
          />
        )}
        {secrets.map((secret) => (
          <SecretRow key={secret.name} secret={secret} onDelete={() => setDeleting(secret.name)} />
        ))}
      </ListCard>
      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Delete {deleting}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              The agent's chats stop getting it from their next message. Its value can't be shown
              again: setting it later means pasting it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full text-[13px]">Cancel</AlertDialogCancel>
            <button
              className={confirmDangerCls}
              onClick={() => {
                if (deleting) remove.mutate({ id: agentId, name: deleting });
                setDeleting(null);
              }}
            >
              Delete
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
